import { getActivityIndex } from '../../utils/getActivityIndex';
import Definition from '@client/config/definition';
import unitHelper from '@client/helper/unit';
import { SportMode } from '@mytypes/activity-detail/ApiModel/SportMode';
import { i18n } from '@client/helper/i18n';
import { h, Ref } from 'vue';
import { BottomTableColumnDefine } from '../types';
import { EMPTY_TEXT, EMPTY_TEXT_SINGLE } from '@client/config/other';
import { fieldFormatConfig } from '../../../../config/field-format';
import { statisticSum, statisticMax } from '../../utils/columnDefinitionUtil';
import { UnitType } from '@mytypes/activity-detail/Common';
import * as corosSportConfig from '@coros/sport-config';
import type { IGradingSystemDataItem } from '@coros/sport-config';
const { getGradingGrade, CLIMB_STYLE_DICT } = corosSportConfig.sport.climb;

const statisticHtml = (value = '', unit = '') => {
  return [value, unit];
};

type Inject = {
  listData: Ref<any>;
  isProgram: boolean;
  sportName: string;
  unitType: UnitType;
  sportMode: SportMode;
  isAllDistanceRef: Ref<boolean>;
};

// 室内攀岩、抱石、户外攀岩列配置
export function getClimbColumns(inject: Inject): {
  indoorClimbOrBoulderingColumns: Record<string, BottomTableColumnDefine>;
  climbingColumns: Record<string, BottomTableColumnDefine>;
} {
  const { t } = i18n;
  const { listData, isProgram, unitType, sportName, sportMode, isAllDistanceRef } = inject;

  // 最大冲坠（Max Activity Fall）
  const MaxActivityFall: BottomTableColumnDefine = {
    title: t('H9034'),
    status: true,
    canSetStatus: true,
    showTips: true,
    render({ record: rowData, rowIndex: index }) {
      if (unitHelper.isNoValue(rowData.maxActivityFall)) {
        return EMPTY_TEXT;
      }
      let distance = rowData.maxActivityFall / 100;
      const [value, unit] = fieldFormatConfig.MaxActivityFall.format(distance, unitType);
      return `${value}${unit}`;
    },
    statistic(data, key = 'maxActivityFall') {
      let distance = statisticMax(data, key) / 100;
      return fieldFormatConfig.MaxActivityFall.format(distance, unitType);
    }
  };

  // 室内攀岩、抱石专用，总上升
  const Total_Ascent: BottomTableColumnDefine = {
    title: t('H9035'),
    status: true,
    canSetStatus: true,
    showTips: true,
    _renderValue(val) {},
    render({ record: rowData, rowIndex: index }) {
      if (unitHelper.isNoValue(rowData.elevGain)) {
        return EMPTY_TEXT;
      }
      const [value, unit] = fieldFormatConfig.Total_Ascent.format(rowData.elevGain / 10, unitType);
      return `${value}${unit}`;
    },
    statistic(data, key = 'elevGain') {
      return fieldFormatConfig.Total_Ascent.format(statisticSum(data, key) / 10, unitType);
    }
  };

  // 室内攀岩、抱石专用，恢复时间
  const RecoverTime: BottomTableColumnDefine = {
    title: t('H1217'),
    status: true,
    canSetStatus: true,
    showTips: true,
    render({ record: rowData, rowIndex: index }) {
      const unit = '';
      const time = rowData.mode3RecoverTime;
      const noValue = unitHelper.isNoValue(time);
      return noValue ? EMPTY_TEXT : `${unitHelper.s2hms(Math.round(time / 100))}${unit}`;
    },
    statistic() {
      return [EMPTY_TEXT_SINGLE, EMPTY_TEXT_SINGLE];
    },
    frequencyListStatistic() {
      return [EMPTY_TEXT_SINGLE, EMPTY_TEXT_SINGLE];
    }
  };

  const Routes: BottomTableColumnDefine = {
    title: sportName === 'Bouldering' ? t('H9095') : t('H9039'),
    status: true,
    canSetStatus: true,
    showTips: true,
    render({ record: rowData, rowIndex }) {
      let value;
      // 关联训练的运动不一样
      if (isProgram) {
        value = rowData.setIndex;
        if (value <= 1) return 1;
      } else {
        value = rowData.rowIndex;
      }
      if (!value) {
        return EMPTY_TEXT;
      }
      return value;
    },
    statistic() {
      return [EMPTY_TEXT_SINGLE, EMPTY_TEXT_SINGLE];
    },
    frequencyListStatistic({ dataInFrameSelection: data }, key) {
      return [EMPTY_TEXT_SINGLE, EMPTY_TEXT_SINGLE];
    }
  };

  const Style: BottomTableColumnDefine = {
    title: t('H9032'),
    status: true,
    canSetStatus: true,
    showTips: true,
    render({ record: rowData, rowIndex: index }) {
      if (unitHelper.isNoValue(rowData.style)) {
        return EMPTY_TEXT;
      }

      return CLIMB_STYLE_DICT[rowData.style]?.i18n ? t(CLIMB_STYLE_DICT[rowData.style].i18n) : EMPTY_TEXT;
    },
    statistic() {
      return [EMPTY_TEXT_SINGLE, EMPTY_TEXT_SINGLE];
    }
  };

  const Falls: BottomTableColumnDefine = {
    title: t('H9033'),
    status: true,
    canSetStatus: true,
    showTips: true,
    render({ record: rowData, rowIndex: index }) {
      if (unitHelper.isNoValue(rowData.falls)) {
        return EMPTY_TEXT;
      }

      return rowData.falls;
    },
    statistic(data, key = 'falls') {
      let value = statisticSum(data, key);
      return [value, ''];
    }
  };

  // 难度
  const Hardest_Grade: BottomTableColumnDefine = {
    title: t('H5046'),
    status: true,
    canSetStatus: true,
    showTips: true,
    render({ record: rowData }) {
      const isIndoorClimbOrBouldering = ['IndoorClimb', 'Bouldering'].includes(sportName);
      const commonGrade = getGradingGrade(isIndoorClimbOrBouldering, rowData.gradingSystem, rowData.hardestGrade);
      if (!commonGrade) {
        return EMPTY_TEXT;
      }
      return commonGrade.gradeNameI18n ? t(commonGrade.gradeNameI18n) : commonGrade.gradeName;
    },
    statistic(data) {
      const isIndoorClimbOrBouldering = ['IndoorClimb', 'Bouldering'].includes(sportName);
      let max = null;
      let maxCommonGrade = 0;
      data
        .filter((i) => i.gradingSystem && i.hardestGrade !== 255)
        .forEach((rowData) => {
          const thisInfo: IGradingSystemDataItem | null = getGradingGrade(
            isIndoorClimbOrBouldering,
            rowData.gradingSystem,
            rowData.hardestGrade
          );
          if (!thisInfo) {
            return;
          }

          const thisInfoCommonGrade = thisInfo.commonGrade[thisInfo.commonGrade.length - 1];
          if (!max || thisInfoCommonGrade > maxCommonGrade) {
            max = thisInfo;
            maxCommonGrade = thisInfoCommonGrade;
          }
        });

      if (!max) {
        return EMPTY_TEXT;
      }

      const unit = '';

      const value = max.gradeNameI18n ? t(max.gradeNameI18n) : max.gradeName;

      return statisticHtml(value, unit);
    }
  };

  const columns: Record<string, BottomTableColumnDefine> = {
    // 室内攀岩、抱石专用 路线
    Routes,
    // 完攀情况（Style）
    Style,
    // "坠落次数（ Falls）"
    Falls,

    // 最大冲坠（Max Activity Fall）
    MaxActivityFall,
    // 总上升（Total Ascent）
    Total_Ascent,
    RecoverTime,
    Hardest_Grade
  };

  return {
    indoorClimbOrBoulderingColumns: columns,
    climbingColumns: {
      Hardest_Grade,
      // 绳距
      Pitches: {
        title: t('H5043'),
        status: true,
        canSetStatus: true,
        showTips: true,
        render({ record: rowData, rowIndex: index }) {
          // TODO: getActivityIndex 已经废弃，这里待处理
          // return rowData.pitchesText || ''
          return getActivityIndex(
            {
              listData,
              data: rowData,
              index,
              isProgram,
              sportName,
              sportMode
            },
            { isAllDistanceRef }
          );
        }
      }
    }
  };
}
